import { isMock } from "@/mocks";
import {
  courseMarketingKitQueryOptions,
  marketingKitIndex,
} from "@/queries/marketing-kit/courseMarketingKitQuery";
import { MarketingKitItem, CHARACTER_TYPE } from "@vixi/types";
import { QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import api, { courseByIdQueryOptions, formdataFromObj } from "@vixi/api";
import { assert, logger, sleepAsync } from "@vixi/utils";

const endpointInfo = [
  {
    endpoint: api.endpoints.generateInstagramPost,
    characterType: CHARACTER_TYPE.INSTAGRAM_POST_CHARACTER,
  },
  {
    endpoint: api.endpoints.generateInstagramStory,
    characterType: CHARACTER_TYPE.INSTAGRAM_STORY_CHARACTER,
  },
  {
    endpoint: api.endpoints.generateLinkedInPost,
    characterType: CHARACTER_TYPE.LINKEDIN_POST_CHARACTER,
  },
] satisfies { endpoint: string; characterType: MarketingKitItem["characterType"] }[];

const generateMarketingKit = (queryClient: QueryClient) => async (courseId: number) => {
  const course = await queryClient.ensureQueryData(courseByIdQueryOptions(queryClient, courseId));

  const character = course.courseCharacters.find(
    (character) => character.characterType === CHARACTER_TYPE.COURSE_CHARACTER,
  );
  assert(!!character, "Course character is required to generate marketing kit");

  const image = await axios.get<File>(character.url, {
    responseType: "blob",
  });
  assert(image.status === 200, "Failed to fetch character image");

  // Initialize array with undefined values
  const initialData: (MarketingKitItem | undefined)[] = Array(
    Object.keys(marketingKitIndex).length,
  ).fill(undefined);

  // Get current data or use initial data
  const currentData = queryClient.getQueryData<MarketingKitItem[]>(
    courseMarketingKitQueryOptions(queryClient, courseId).queryKey,
  ) ?? initialData;

  // Set initial data to show loading state
  queryClient.setQueryData<MarketingKitItem[]>(
    courseMarketingKitQueryOptions(queryClient, courseId).queryKey,
    currentData,
  );

  const response = await Promise.all(
    endpointInfo.map(async ({ endpoint, characterType }) => {
      const res = await api.post<Partial<MarketingKitItem>>(
        endpoint,
        formdataFromObj({
          image: image.data,
          courseId,
        }),
      );

      const marketingKitItem = { ...res.data, characterType };

      // Update data optimistically as each request completes
      queryClient.setQueryData<MarketingKitItem[]>(
        courseMarketingKitQueryOptions(queryClient, courseId).queryKey,
        (oldData) => {
          // Ensure array has correct length
          const baseData = oldData ?? initialData;
          const newData = Array(Object.keys(marketingKitIndex).length)
            .fill(undefined)
            .map((_, i) => baseData[i] ?? undefined);
          
          const index = marketingKitIndex[characterType];
          if (index !== undefined) {
            newData[index] = marketingKitItem as MarketingKitItem;
          } else {
            logger.warn("Unknown character type", characterType);
          }
          return newData;
        },
      );

      return marketingKitItem satisfies Partial<MarketingKitItem>;
    }),
  );

  // Don't invalidate queries - we're updating data directly with setQueryData
  // This prevents unnecessary refetches that cause loading delays

  return response;
};

const mockGenerateMarketingKit = (queryClient: QueryClient) => async (courseId: number) => {
  logger.log("Mocking generateMarketingKit", courseId);

  // Initialize array with undefined values
  const initialData: (MarketingKitItem | undefined)[] = Array(
    Object.keys(marketingKitIndex).length,
  ).fill(undefined);

  // Get current data or use initial data
  const currentData = queryClient.getQueryData<MarketingKitItem[]>(
    courseMarketingKitQueryOptions(queryClient, courseId).queryKey,
  ) ?? initialData;

  // Set initial data to show loading state
  queryClient.setQueryData<MarketingKitItem[]>(
    courseMarketingKitQueryOptions(queryClient, courseId).queryKey,
    currentData,
  );

  const response = await Promise.all(
    endpointInfo.map(async (info, i) => {
      await sleepAsync((i + 1) * 2000);

      const mockItem = {
        characterType: info.characterType,
      } as Partial<MarketingKitItem>;

      // Update data optimistically as each request completes
      queryClient.setQueryData<MarketingKitItem[]>(
        courseMarketingKitQueryOptions(queryClient, courseId).queryKey,
        (oldData) => {
          // Ensure array has correct length
          const baseData = oldData ?? initialData;
          const newData = Array(Object.keys(marketingKitIndex).length)
            .fill(undefined)
            .map((_, i) => baseData[i] ?? undefined);
          
          const index = marketingKitIndex[info.characterType];
          if (index !== undefined) {
            newData[index] = mockItem as MarketingKitItem;
          }
          return newData;
        },
      );

      return mockItem;
    }),
  );

  // Don't invalidate queries - we're updating data directly with setQueryData
  // This prevents unnecessary refetches that cause loading delays

  return response;
};

const getMutationFn = isMock("marketingKit") ? mockGenerateMarketingKit : generateMarketingKit;

export const useGenerateMarketingKit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: getMutationFn(queryClient),
  });
};
